/**
 * Nominatim geocoding client for address search.
 * Uses the free OpenStreetMap Nominatim API (no API key required).
 * Privacy-friendly: no tracking, OSM-based (same data source as our map).
 *
 * Usage policy: max 1 request/second, include a descriptive User-Agent.
 * https://nominatim.org/release-docs/develop/api/Search/
 */

export interface SearchResult {
  placeId: number;
  displayName: string;
  lat: number;
  lng: number;
  type: string;
  importance: number;
}

/**
 * Search for addresses/places in Germany using Nominatim.
 * Results are limited to Germany for relevance.
 */
export async function searchAddress(query: string): Promise<SearchResult[]> {
  if (query.trim().length < 3) return [];

  const params = new URLSearchParams({
    q: query,
    format: "json",
    countrycodes: "de",
    limit: "5",
    addressdetails: "1",
  });

  const url = `https://nominatim.openstreetmap.org/search?${params}`;

  const response = await fetch(url, {
    headers: {
      "Accept": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Nominatim HTTP ${response.status}`);
  }

  const data = (await response.json()) as Array<{
    place_id: number;
    display_name: string;
    lat: string;
    lon: string;
    type: string;
    importance: number;
  }>;

  return data.map((item) => ({
    placeId: item.place_id,
    displayName: item.display_name,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
    type: item.type,
    importance: item.importance,
  }));
}
